// WORK COMPOSER surface — the operator console that reviews a drafted Workflow Definition across
// the 8-step rail and carries it to the ONE activation gate (brief §7 v2; prototype composerView).
// Thin over services: it fetches WorkApi.getComposer through the injected transport and renders the
// drafted configuration VERBATIM (G-182 — the anti-omission guarantee, brief §3.1); it routes no
// verdict write and sets no approval field (P-GATE). OPERATOR-GATED at the edge: the shell mounts it
// for operators only, and a non-operator role still gets the honest console note — rendering only;
// the owning read interface is the real boundary (G-180). Navigation is callbacks-only
// (surface-contracts); honest loading/unavailable states come from SurfaceFrame (G-197: no internal
// detail on failure).

import { useWorkApi } from '../api-client.ts';
import type { WorkComposerProjection } from '../projections.ts';
import type { WorkComposerSurfaceProps } from '../surface-contracts.ts';
import { Panel } from '../../primitives.tsx';
import { SurfaceFrame } from '../../surface-frame.tsx';
import { useProjection } from '../../useProjection.ts';
import { ComposerLoaded } from './components/ComposerLoaded.tsx';

function OperatorOnlyNote(): React.JSX.Element {
  return (
    <Panel className="px-5 py-6">
      <p role="status" className="text-sm text-text-muted">
        The Composer is the operator console.
      </p>
      <p className="mt-1 text-sm text-text-subtle">
        An operator reviews and activates workflows here. Nothing on this screen is available to
        this account.
      </p>
    </Panel>
  );
}

function ComposerConsole({
  onBack,
  onOpenBoard,
}: {
  readonly onBack: () => void;
  readonly onOpenBoard: (definitionId: string) => void;
}): React.JSX.Element {
  const api = useWorkApi();
  const state = useProjection<WorkComposerProjection>(api.getComposer);
  return (
    <SurfaceFrame state={state}>
      {(config) => <ComposerLoaded config={config} onBack={onBack} onOpenBoard={onOpenBoard} />}
    </SurfaceFrame>
  );
}

/** The Composer surface. The parent mounts it for operators; the role prop is re-checked here. */
export function WorkComposerSurface({
  role,
  onBack,
  onOpenBoard,
}: WorkComposerSurfaceProps): React.JSX.Element {
  return (
    <section aria-label="Compose a workflow">
      {role === 'operator' ? (
        <ComposerConsole onBack={onBack} onOpenBoard={onOpenBoard} />
      ) : (
        <OperatorOnlyNote />
      )}
    </section>
  );
}
